import { Controller } from '@hotwired/stimulus'

// Loads the next page of a list when this element scrolls into view and appends its entries to the list.
// The fetched page names the following page (or has none, then the loader goes away).
export default class extends Controller {
  static values = { url: String, container: String }

  connect() {
    this.loading = false
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.load()
    }, { rootMargin: '300px' })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  async load() {
    const list = document.querySelector(this.containerValue)
    if (this.loading || !list) return

    this.loading = true
    let failed = false
    try {
      const url = this.sameOriginUrl(this.urlValue)
      const response = await fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      if (!response.ok) throw new Error(`Page request failed: ${response.status}`)

      const page = new DOMParser().parseFromString(await response.text(), 'text/html')
      const entries = Array.from(page.querySelector(this.containerValue)?.children || [])
      entries.forEach((entry) => entry.querySelectorAll('script').forEach((script) => script.remove()))
      list.append(...entries)

      const next = page.querySelector('[data-controller~="infinite-scroll"]')
      if (next) this.urlValue = this.sameOriginUrl(next.dataset.infiniteScrollUrlValue).href
      else this.element.remove()
    } catch (error) {
      failed = true
      console.error(error)
    } finally {
      this.loading = false
      // after a failure only scrolling the loader into view again retries, no request loop
      if (!failed) this.retriggerWhenStillVisible()
    }
  }

  // Only pages of this app are loaded, whatever the markup says
  sameOriginUrl(value) {
    const url = new URL(value, window.location.href)
    if (url.origin !== window.location.origin) throw new Error(`Refusing to load ${url.origin}`)

    return url
  }

  // The observer only reports changes: a short page can leave the loader in view, look again.
  retriggerWhenStillVisible() {
    if (!this.element.isConnected) return

    this.observer.unobserve(this.element)
    this.observer.observe(this.element)
  }
}
